import type { CourseProfile } from '@/app/services/courseProfileService';
import type { CuratedCourseWiki } from '@/app/courses/courseWikiTypes';
import type { ProfessorLinkPreview } from '@/app/professors/professorProfileContent';

export type CourseDetailRequirementList = { key: string; label: string; description?: string };

/**
 * Serializable route presentation contract. Data loading, identifier resolution,
 * metadata, and not-found behavior remain owned by `[courseId]/page.tsx`.
 */
export type CourseDetailViewModel = {
  prerequisites?: {id:string;title:string}[];
  profile: CourseProfile;
  wiki: CuratedCourseWiki | null;
  requirementLists: CourseDetailRequirementList[];
  professorPreviews: Record<string, ProfessorLinkPreview>;
  latestTerm: string | null;
  latestSections: CourseProfile['sections'];
  openSections: CourseProfile['sections'];
  campuses: string[];
  instructors: string[];
  sniperIndex: string | undefined;
  loadedTerms: number;
  assistantPrompt: string;
};

export function createCourseDetailViewModel(
  profile: CourseProfile,
  options: {
    prerequisites?: {id:string;title:string}[];
    wiki?: CuratedCourseWiki | null;
    requirementLists?: CourseDetailRequirementList[];
    professorPreviews?: Record<string, ProfessorLinkPreview>;
  } = {},
): CourseDetailViewModel {
  const latestTerm = profile.sections[0]?.semesterLabel ?? null;
  const latestSections = latestTerm ? profile.sections.filter((section) => section.semesterLabel === latestTerm) : [];
  const openSections = latestSections.filter((section) => section.open);
  return {
    profile,
    prerequisites:options.prerequisites??[],
    wiki: options.wiki ?? null,
    requirementLists: options.requirementLists ?? [],
    professorPreviews: options.professorPreviews ?? {},
    latestTerm, latestSections, openSections,
    campuses: Array.from(new Set(latestSections.map((section) => section.campus).filter((campus) => campus && campus !== 'TBA'))),
    instructors: Array.from(new Set(latestSections.flatMap((section) => section.instructors).filter((name) => name && name !== 'TBA'))),
    sniperIndex: openSections[0]?.registrationIndex ?? latestSections[0]?.registrationIndex,
    loadedTerms: new Set(profile.sections.map((section) => section.semesterKey)).size,
    assistantPrompt: `Help me evaluate ${profile.displayId} ${profile.fullTitle} for my degree plan and schedule.`,
  };
}

export function formatCourseScore(value: number | null | undefined) { return value == null ? 'Not available' : value.toFixed(2); }
export function formatCoursePercent(value: number | null | undefined) { return value == null ? 'Not available' : `${value.toFixed(1)}%`; }

export function formatCourseDepartment(value: string | null | undefined, subject: string) {
  if (!value) return `Rutgers subject ${subject}`;
  return value.toLowerCase().replace(/\b[a-z]/g, (letter) => letter.toUpperCase());
}
